import { APP_ERROR_MESSAGES } from '@/config/app-error-messages';
import publicAxios from '@/utils/public-axios';

class TokenService {
    private static instance: TokenService;

    private accessToken: string | null = null;

    private subscribers: ((token: string) => void)[] = [];

    private refreshToken: string | null = null;

    private constructor() {
        // Private constructor to enforce singleton pattern
        this.accessToken = null;
        this.subscribers = [];
        this.refreshToken = null;
        this.syncRefreshToken();
    }

    static getInstance(): TokenService {
        if (!TokenService.instance) {
            TokenService.instance = new TokenService();
        }
        return TokenService.instance;
    }

    setAccessToken(token: string | null): void {
        this.accessToken = token;
        // Note: To avoid circular dependencies with axiosInstance,
        // we do not directly mutate axiosInstance.defaults here.
        // Instead, the request interceptor in axios-instance.ts will read getAccessToken().
    }

    getAccessToken(): string | null {
        return this.accessToken;
    }

    // For token refresh notifications
    subscribe(callback: (token: string) => void): () => void {
        this.subscribers.push(callback);
        return () => {
            this.subscribers = this.subscribers.filter((cb) => cb !== callback);
        };
    }

    notifySubscribers(token: string): void {
        this.subscribers.forEach((callback) => callback(token));
    }

    private syncRefreshToken(): void {
        try {
            this.refreshToken = localStorage.getItem('refresh_token');
        } catch {
            this.refreshToken = null;
        }
    }

    // Get refresh token from localStorage
    getRefreshToken(): string | null {
        this.syncRefreshToken();
        return this.refreshToken;
    }

    // Store refresh token in localStorage
    setRefreshToken(token: string | null): void {
        this.refreshToken = token;
        try {
            if (token) {
                localStorage.setItem('refresh_token', token);
            } else {
                localStorage.removeItem('refresh_token');
            }
        } catch {
            // Silent fail for localStorage access errors
        }
    }

    // Refresh access token using refresh token
    async refreshAccessToken(): Promise<string | null> {
        try {
            const refreshToken = this.getRefreshToken();

            if (!refreshToken) {
                throw new Error(APP_ERROR_MESSAGES.NO_REFRESH_TOKEN_AVAILABLE);
            }

            const response = await publicAxios.post('auth/refresh', { refreshToken });

            const responseData = response.data as
                | {
                      accessToken?: string;
                      refreshToken?: string;
                  }
                | undefined;

            const accessToken = responseData?.accessToken ?? null;
            const newRefreshToken = responseData?.refreshToken ?? null;

            if (!accessToken) {
                throw new Error(APP_ERROR_MESSAGES.ACCESS_TOKEN_MISSING);
            }

            this.setAccessToken(accessToken);
            if (newRefreshToken) {
                this.setRefreshToken(newRefreshToken);
            }

            this.notifySubscribers(accessToken);
            return accessToken;
        } catch (error) {
            this.setAccessToken(null);
            this.setRefreshToken(null);
            throw error;
        }
    }

    clearTokens(): void {
        this.setAccessToken(null);
        this.setRefreshToken(null);
    }
}

export default TokenService.getInstance();
